import type { MasonryComponentEntity } from "building-types/floors/masonryComponentTypes";
import { MasonryType } from "building-types/floors/masonryComponentTypes";
import { type SheathingComponentEntity, SheathingType } from "building-types/floors/sheathingComponentTypes";
import { type SlabComponentEntity, SlabType } from "building-types/floors/slabComponentTypes";
import {
  PlywoodProfileToThickness,
  PlywoodProfile,
  SpanRating,
  OSBProfile,
  OSBProfileToThickness,
} from "building-types/framing/lumberTypes";
import { CMUProfile } from "building-types/masonry/masonryTypes";
import type { Table } from "common/data/table";

export const builtInSlabFloorComponentsGroupId = "builtInSlabFloorComponentsGroup";
export const defaultSlabComponentId = "defaultSlab";

export const builtInSlabFloorComponents: Table<SlabComponentEntity> = {
  [defaultSlabComponentId]: {
    entityType: "SLAB_COMPONENT",
    id: defaultSlabComponentId,
    name: "Standard Concrete Slab",
    slabType: SlabType.CIP,
    thickness: 6,
    sortIndex: 0,
  },
};

export function isBuiltInSlabFloorComponent(id: string): boolean {
  return Object.keys(builtInSlabFloorComponents).includes(id);
}

export const builtInSheathingComponentsGroupId = "builtInSheathingComponentsGroup";

export const builtInSheathingComponents = {
  ["defaultSheathing"]: {
    entityType: "SHEATHING_COMPONENT",
    id: "defaultSheathing",
    name: '3/4" Plywood',
    sheathingType: SheathingType.PLYWOOD,
    thickness: PlywoodProfileToThickness[PlywoodProfile["3/4-u"]],
    profile: PlywoodProfile["3/4-u"],
    spanRating: SpanRating.SR16,
    sortIndex: 0,
  },
  ["1/4-drywall"]: {
    entityType: "SHEATHING_COMPONENT",
    id: "1/4-drywall",
    name: '1/4" Drywall',
    sheathingType: SheathingType.DRYWALL,
    thickness: 1 / 4,
    sortIndex: 1,
  },
  ["3/8-drywall"]: {
    entityType: "SHEATHING_COMPONENT",
    id: "3/8-drywall",
    name: '3/8" Drywall',
    sheathingType: SheathingType.DRYWALL,
    thickness: 3 / 8,
    sortIndex: 2,
  },
  ["1/2-drywall"]: {
    entityType: "SHEATHING_COMPONENT",
    id: "1/2-drywall",
    name: '1/2" Drywall',
    sheathingType: SheathingType.DRYWALL,
    thickness: 1 / 2,
    sortIndex: 3,
  },
  ["5/8-drywall"]: {
    entityType: "SHEATHING_COMPONENT",
    id: "5/8-drywall",
    name: '5/8" Drywall',
    sheathingType: SheathingType.DRYWALL,
    thickness: 5 / 8,
    sortIndex: 4,
  },
  ["7/16-osb"]: {
    entityType: "SHEATHING_COMPONENT",
    id: "7/16-osb",
    name: '7/16" OSB',
    sheathingType: SheathingType.OSB,
    thickness: OSBProfileToThickness[OSBProfile["7/16"]],
    profile: OSBProfile["7/16"],
    spanRating: SpanRating.SR16,
    sortIndex: 5,
  },
} satisfies Table<SheathingComponentEntity>;

/**
 * @TODO WALLS V2 @jaggyluis - rename to 3/4"-ply and write migration
 * since this is stored on the doc
 */
export const defaultFloorSheathingComponentId: keyof typeof builtInSheathingComponents = "defaultSheathing";
export const defaultWallInteriorSheathingComponentId: keyof typeof builtInSheathingComponents = "1/2-drywall";
export const defaultWallExteriorSheathingComponentId: keyof typeof builtInSheathingComponents = "7/16-osb";

export function isBuiltInSheathingComponent(id: string): boolean {
  return Object.keys(builtInSheathingComponents).includes(id);
}

export const builtInMasonryComponentsGroupId = "builtInMasonryComponentsGroup";

export const builtInMasonryComponents = {
  ["cmu-4"]: {
    entityType: "MASONRY_COMPONENT",
    id: "cmu-4",
    name: '4" CMU',
    masonryType: MasonryType.CONCRETE,
    profile: CMUProfile.CMU_4,
    sortIndex: 0,
  },
  ["cmu-6"]: {
    entityType: "MASONRY_COMPONENT",
    id: "cmu-6",
    name: '6" CMU',
    masonryType: MasonryType.CONCRETE,
    profile: CMUProfile.CMU_6,
    sortIndex: 1,
  },
  ["cmu-8"]: {
    entityType: "MASONRY_COMPONENT",
    id: "cmu-8",
    name: '8" CMU',
    masonryType: MasonryType.CONCRETE,
    profile: CMUProfile.CMU_8,
    sortIndex: 2,
  },
  ["cmu-10"]: {
    entityType: "MASONRY_COMPONENT",
    id: "cmu-10",
    name: '10" CMU',
    masonryType: MasonryType.CONCRETE,
    profile: CMUProfile.CMU_10,
    sortIndex: 3,
  },
  ["cmu-12"]: {
    entityType: "MASONRY_COMPONENT",
    id: "cmu-12",
    name: '12" CMU',
    masonryType: MasonryType.CONCRETE,
    profile: CMUProfile.CMU_12,
    sortIndex: 4,
  },
} satisfies Table<MasonryComponentEntity>;

export const defaultWallMasonryComponentId: keyof typeof builtInMasonryComponents = "cmu-8";

export function isBuiltInMasonryComponent(id: string): boolean {
  return Object.keys(builtInMasonryComponents).includes(id);
}
